const dataMap = new WeakMap();

/**
 * Normalizes a Float32Array to Array(m): We use this to draw amplitudes on a graph
 * If we're rendering the same audio data, then we'll often be using
 * the same (data, m, downsamplePeaks) triplets so we give option to memoize
 */
const normalizeArray = (data, m, downsamplePeaks = false, memoize = false) => {
  let cache, mKey, dKey;
  if (memoize) {
    mKey = m.toString();
    dKey = downsamplePeaks.toString();
    cache = dataMap.has(data) ? dataMap.get(data) : {};
    dataMap.set(data, cache);
    cache[mKey] = cache[mKey] || {};
    if (cache[mKey][dKey]) {
      return cache[mKey][dKey];
    }
  }
  const n = data.length;
  const result = new Array(m);
  if (m <= n) {
    // Downsampling
    result.fill(0);
    const count = new Array(m).fill(0);
    for (let i = 0; i < n; i++) {
      const index = Math.floor(i * (m / n));
      if (downsamplePeaks) {
        // take highest result in the set
        result[index] = Math.max(result[index], Math.abs(data[i]));
      } else {
        result[index] += Math.abs(data[i]);
      }
      count[index]++;
    }
    if (!downsamplePeaks) {
      for (let i = 0; i < result.length; i++) {
        result[i] = result[i] / count[i];
      }
    }
  } else {
    for (let i = 0; i < m; i++) {
      const index = (i * (n - 1)) / (m - 1);
      const low = Math.floor(index);
      const high = Math.ceil(index);
      const t = index - low;
      if (high >= n) {
        result[i] = data[n - 1];
      } else {
        result[i] = data[low] * (1 - t) + data[high] * t;
      }
    }
  }
  if (memoize) {
    cache[mKey][dKey] = result;
  }
  return result;
};

export const WavRenderer = {
  /**
   * Renders a point-in-time snapshot of an audio sample, usually frequency values
   * @param canvas
   * @param ctx
   * @param data
   * @param color
   * @param pointCount number of bars to render
   * @param barWidth width of bars in px
   * @param barSpacing spacing between bars in px
   * @param center vertically center the bars
   */
  drawBars: (canvas, ctx, data, color, pointCount, radius) => {
    const points = normalizeArray(data, pointCount, true);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const angleStep = (2 * Math.PI) / pointCount; // Angle between bars

    for (let i = 0; i < pointCount; i++) {
      const amplitude = Math.abs(points[i]);
      const barHeight = Math.max(1, (amplitude * canvas.height) / 3); // Bar height proportional to amplitude

      const startRadius = radius; // Inner radius of the circle
      const endRadius = startRadius + barHeight;

      const startAngle = i * angleStep;
      const endAngle = startAngle + angleStep;

      // Draw the bar as a radial section
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(
        centerX + Math.cos(startAngle) * startRadius,
        centerY + Math.sin(startAngle) * startRadius
      );
      ctx.lineTo(
        centerX + Math.cos(startAngle) * endRadius,
        centerY + Math.sin(startAngle) * endRadius
      );
      ctx.lineTo(
        centerX + Math.cos(endAngle) * endRadius,
        centerY + Math.sin(endAngle) * endRadius
      );
      ctx.lineTo(
        centerX + Math.cos(endAngle) * startRadius,
        centerY + Math.sin(endAngle) * startRadius
      );
      ctx.closePath();
      ctx.fill();
    }
  },

  drawRings: (canvas, ctx, data, color, ringCount = 10, baseRadius = 150) => {
    const amplitudes = normalizeArray(data, ringCount, true);
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    for (let i = 0; i < ringCount; i++) {
      const amplitude = amplitudes[i];
      const radius = baseRadius + amplitude * 100; // Adjust size of ring based on amplitude

      // Calculate inverse thickness
      const lineWidth = 15 - amplitude * 14; // Thicker when amplitude is lower, thinner when amplitude is higher
      const clampedLineWidth = Math.max(1, lineWidth); // Prevent lineWidth from going below 2px

      // Draw the ring
      ctx.strokeStyle = color;
      ctx.lineWidth = clampedLineWidth;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  },
};
